import { useEffect, useState } from 'react';
import AlbumOutlinedIcon from '@mui/icons-material/AlbumOutlined';
import { Button, Dialog } from '../ui';

export interface ClaimMigrateSummary {
  tracks: number;
  projects: number;
  mbUsed: number;
}

export interface ClaimMigrateDialogProps {
  open: boolean;
  summary: ClaimMigrateSummary;
  onContinue: () => void;
  autoContinueMs?: number;
}

const BORDER_SOFT = '#1c1f24';
const BG = '#0C111D';
const SURFACE_2 = '#1b1e26';

const ClaimMigrateDialog: React.FC<ClaimMigrateDialogProps> = ({
  open,
  summary,
  onContinue,
  autoContinueMs = 4000,
}) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!open) {
      setElapsed(0);
      return;
    }
    const start = performance.now();
    let rafId: number;
    const tick = (now: number) => {
      const e = Math.min(autoContinueMs, now - start);
      setElapsed(e);
      if (e >= autoContinueMs) {
        onContinue();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [open, autoContinueMs, onContinue]);

  const progress = autoContinueMs > 0 ? (elapsed / autoContinueMs) * 100 : 0;
  const secondsLeft = Math.max(0, Math.ceil((autoContinueMs - elapsed) / 1000));

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onContinue();
      }}
    >
      <Dialog.Content className="max-w-[520px]" aria-describedby={undefined}>
        <div className="p-[36px_38px_30px] text-center font-sans">
          <div
            className="mx-auto mb-5 grid h-[76px] w-[76px] place-items-center rounded-full text-cyan"
            style={{
              background: 'rgba(39,182,203,0.14)',
              border: '1px solid rgba(39,182,203,0.32)',
            }}
          >
            <AlbumOutlinedIcon style={{ fontSize: 36 }} />
          </div>
          <Dialog.Title className="text-[22px] font-bold text-white">
            Your tracks made it in
          </Dialog.Title>
          <div className="mx-auto mt-[11px] max-w-[400px] text-[14.5px] leading-[1.55] text-muted">
            Everything you uploaded as a guest has been added to your new account.
          </div>
          <div className="m-[22px_0_6px] flex justify-center gap-[10px]">
            {[
              { n: summary.tracks, l: summary.tracks === 1 ? 'track' : 'tracks' },
              {
                n: summary.projects,
                l: summary.projects === 1 ? 'project' : 'projects',
              },
              { n: summary.mbUsed, l: 'MB used' },
            ].map((item) => (
              <div
                key={item.l}
                className="max-w-[130px] flex-1 rounded-[12px] p-[14px_10px]"
                style={{ background: BG, border: `1px solid ${BORDER_SOFT}` }}
              >
                <div className="text-[22px] font-bold text-white">{item.n}</div>
                <div className="mt-[2px] text-[12px] text-muted">{item.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-[26px] flex flex-col gap-[11px]">
            <Button variant="primary" size="lg" fullWidth onClick={onContinue}>
              Continue to dashboard
            </Button>
          </div>
        </div>
        <div aria-hidden className="h-[4px]" style={{ background: SURFACE_2 }}>
          <div className="h-full" style={{ width: `${progress}%`, background: 'var(--cyan)' }} />
        </div>
        <div className="py-[10px] text-center font-sans text-[12px] text-faint">
          Continuing automatically in {secondsLeft}s…
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ClaimMigrateDialog;
